import React, { useState } from "react";
import { usePlanner } from "../../contexts/PlannerContext";
import { usePricingSettings } from "../../contexts/PricingSettingsContext";
import { apiFetch } from "../../lib/apiFetch";

function formatShort(dateIso) {
  if (!dateIso) return "--";
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function ScheduleCoachPanel({ schedule, dueDate, batchLabel, batchId }) {
  const [aiCoach, setAiCoach] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [plannerMessage, setPlannerMessage] = useState(null);
  const { pricingSettings } = usePricingSettings();
  const aiEnabled = pricingSettings?.enableScheduleAi !== false;
  const { addTasks } = usePlanner();

  if (!schedule || !schedule.stages || schedule.stages.length === 0) {
    return (
      <div className="mudo-card p-4 text-sm space-y-1">
        <h3 className="mudo-section-title">Suggested schedule</h3>
        <p className="text-xs opacity-70">
          Once you&apos;ve set up this batch, Mudo can suggest a simple make + dry + fire + dispatch schedule here.
        </p>
      </div>
    );
  }

  const { stages, warnings } = schedule;

  function buildTasksFromSchedule() {
    if (!schedule?.stages?.length) return [];
    const label = batchLabel || "Batch";

    return schedule.stages.map((stage) => ({
      id: undefined,
      title: `${stage.label} - ${label}`,
      start: stage.startDate,
      end: stage.endDate,
      status: "planned",
      category: "production",
      taskType: stage.label,
      batchId: batchId || null,
      tags: [stage.label, batchId].filter(Boolean),
      source: "schedule-coach",
      notes: "",
      meta: {
        stageId: stage.id,
        stageLabel: stage.label,
        dueDate: dueDate || null,
      },
    }));
  }

  function handleAddToPlanner() {
    const tasks = buildTasksFromSchedule();
    if (!tasks.length || typeof addTasks !== "function") return;
    addTasks(tasks);
    setPlannerMessage(`Added ${tasks.length} stage(s) to Planner.`);
    setTimeout(() => setPlannerMessage(null), 4000);
  }

  async function handleAskScheduleAi() {
    if (!schedule || !schedule.stages || schedule.stages.length === 0) return;
    if (!aiEnabled) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const context = {
        stages: schedule.stages,
        dueDate: dueDate || null,
        warnings: schedule.warnings || [],
      };
      const res = await apiFetch("/api/ai/coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "schedule", context }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "AI error");
      setAiCoach(json.result);
    } catch (err) {
      console.error("[ScheduleCoachPanel] AI error", err);
      setAiError("Mudo AI couldn't review this schedule right now.");
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <div className="mudo-card p-4 text-sm space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="mudo-section-title">Suggested schedule</h3>
        <p className="text-xs opacity-70">
          Draft timeline based on typical drying/firing stages. You can fine-tune this in the Planner.
        </p>
      </div>

      <div className="space-y-1 text-xs">
        {stages.map((s) => (
          <div
            key={s.id}
            className="flex justify-between items-center rounded-xl border border-white/10 bg-black/30 px-3 py-1.5"
          >
            <span className="opacity-80">{s.label}</span>
            <span className="font-mono text-[11px] opacity-90">
              {formatShort(s.startDate)} - {formatShort(s.endDate)}
            </span>
          </div>
        ))}
      </div>

      {warnings && warnings.length > 0 && (
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/5 px-3 py-2 text-[11px] space-y-1">
          {warnings.map((w, idx) => (
            <div key={idx} className="opacity-90">
              - {w}
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] opacity-60">
        This is a guide, not a rule. Use the Planner views to drag, resize and adjust stages as your week changes.
      </p>

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={handleAddToPlanner}
          disabled={!schedule?.stages?.length}
          className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add to Planner
        </button>
        {plannerMessage && <span className="text-[11px] opacity-80">{plannerMessage}</span>}
      </div>

      <div className="border border-dashed border-cyan-500/40 rounded-xl px-3 py-2 text-[11px] space-y-1 mt-2">
        <div className="flex items-center justify-between">
          <span className="uppercase tracking-wide opacity-70">Mudo AI</span>
          <button
            type="button"
            onClick={handleAskScheduleAi}
            disabled={aiLoading || !aiEnabled}
            className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aiLoading ? "Thinking…" : "Review this timeline"}
          </button>
        </div>
        {aiError && <p className="text-[11px] text-red-300 mt-1">{aiError}</p>}
        {aiCoach && !aiError && (
          <div className="mt-1 space-y-1">
            <div className="font-medium opacity-80">{aiCoach.title}</div>
            <p className="opacity-80">{aiCoach.summary}</p>
            {Array.isArray(aiCoach.bullets) && aiCoach.bullets.length > 0 && (
              <ul className="list-disc list-inside opacity-80 space-y-0.5">
                {aiCoach.bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
